import { css } from '@emotion/react';
import { FormControlLabel, MenuItem, Switch, TextField } from '@material-ui/core';
import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import NavResponsiveModal, {
    ModalActions,
    ModalContent,
    ModalHeader,
} from '@/components/Navigation/NavResponsiveModal';
import { GreyBody, PrimaryButton, SecondaryButton } from '@/styles/blocks';
import { SERVICE_TYPES, serviceTypeLabel } from '@/constants/serviceTypes';
import { useServiceCatalogForm } from './hooks';
import { serviceRateFieldState } from './utils';

const spaced = css`
    margin-bottom: 1rem;
`;

// One toggle + its helper text as a stacked block. GreyBody is an inline span, so without
// this the helper text flows alongside the label and the two toggles run together.
const toggleField = css`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin-bottom: 0.5rem;

    /* The Switch carries its own 9px padding, so the last group needs no trailing gap. */
    &:last-of-type {
        margin-bottom: 0;
    }
`;

const helperText = css`
    display: block;
`;

// Add / edit a single service catalog entry. `service` non-null => edit mode.
// These fields are what ingestion stamps onto each order's service lines (matched by
// service code), so editing them here changes how future orders are enriched — existing
// orders keep the values they were stamped with. The rate is the exception: it's a SERVICE_RATE row
// on the shipper's GE tariffs, which pricing reads each time an order prices.
// Service time is entered as HH:MM (matching the SKUs cache editor) and stored as minutes.
// Add mode can be prefilled (`defaults`, catalog-row-shaped) with the code fixed (`codeLocked`) —
// the Edit Order modal's quick add does both, for a line whose code isn't in the catalog yet.
// `serviceRates` is useCatalogServiceRates' result and `serviceRate` the entry's row in it. The form
// stays locked while they load, since the Rate seeds from them (useServiceCatalogForm's `locked`).
// onSave(payload, ratePayload) hands both halves to useSaveServiceCatalog.
const ServiceCatalogModal = ({
    open,
    service,
    onClose,
    onSave,
    loading,
    dimensionFields = [],
    defaults,
    codeLocked = false,
    subtitle,
    serviceRate,
    serviceRates,
}) => {
    const isEdit = !!service;
    const {
        values,
        setField,
        setDimension,
        nonBillableLocked,
        serviceRatesEnabled,
        locked,
        canSave,
        payload,
        ratePayload,
    } = useServiceCatalogForm(open, service, defaults, serviceRate, serviceRates?.loading);
    const rateField = serviceRateFieldState({
        billable: values.billable,
        dimensions: values.dimensions,
        tariffIds: serviceRates?.tariffIds,
        ready: serviceRates?.ready,
        error: serviceRates?.error,
    });

    return (
        <NavResponsiveModal open={open} onClose={onClose}>
            <ModalHeader title={isEdit ? 'Edit Service' : 'Add Service'} subtitle={subtitle} onClose={onClose} />
            <ModalContent>
                <TextField
                    fullWidth
                    autoFocus={!codeLocked}
                    disabled={codeLocked || locked}
                    variant="outlined"
                    label="Code"
                    value={values.code}
                    onChange={(e) => setField('code')(e.target.value)}
                    css={spaced}
                />
                <TextField
                    fullWidth
                    autoFocus={codeLocked}
                    disabled={locked}
                    variant="outlined"
                    label="Description"
                    value={values.description}
                    onChange={(e) => setField('description')(e.target.value)}
                    css={spaced}
                />
                {isEdit
                    ? dimensionFields
                          .filter(({ key }) => values.dimensions?.[key])
                          .map(({ key, label }) => (
                              <TextField
                                  fullWidth
                                  disabled
                                  key={key}
                                  variant="outlined"
                                  label={label}
                                  value={values.dimensions[key]}
                                  css={spaced}
                              />
                          ))
                    : /* Constrained to the values already in use — a new one would have no rate
                         rows to match and would price at $0. */
                      dimensionFields.map(({ key, label, values: options }) => (
                          <TextField
                              select
                              fullWidth
                              disabled={locked}
                              key={key}
                              variant="outlined"
                              label={label}
                              value={values.dimensions?.[key] || ''}
                              onChange={(e) => setDimension(key)(e.target.value)}
                              css={spaced}
                          >
                              <MenuItem value="">
                                  <GreyBody>None</GreyBody>
                              </MenuItem>
                              {options.map((option) => (
                                  <MenuItem key={option} value={option}>
                                      {option}
                                  </MenuItem>
                              ))}
                          </TextField>
                      ))}
                {/* Constrained list, not free text: the GE pricing algo switches on the raw
                    value and prices an unrecognized type at $0. Labels are display-only.
                    Locked to Non Billable for a newly scoped service — see nonBillableLocked. */}
                <TextField
                    select
                    fullWidth
                    variant="outlined"
                    label="Type"
                    disabled={nonBillableLocked || locked}
                    value={values.serviceType}
                    onChange={(e) => setField('serviceType')(e.target.value)}
                    helperText={
                        nonBillableLocked
                            ? 'Scoped services added here are non-billable — their rates are seeded manually, and a billable type with no rate row fails pricing for the whole order.'
                            : ''
                    }
                    css={spaced}
                >
                    <MenuItem value="">
                        <GreyBody>None</GreyBody>
                    </MenuItem>
                    {SERVICE_TYPES.map((type) => (
                        <MenuItem key={type} value={type}>
                            {serviceTypeLabel(type)}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    fullWidth
                    disabled={locked}
                    variant="outlined"
                    label="Size"
                    value={values.size}
                    onChange={(e) => setField('size')(e.target.value)}
                    css={spaced}
                />
                <TextField
                    fullWidth
                    disabled={locked}
                    variant="outlined"
                    label="Service time"
                    placeholder="HH:MM"
                    value={values.serviceTime}
                    onChange={(e) => setField('serviceTime')(e.target.value)}
                    css={spaced}
                />
                {/* The catalog-service-rates flag: the service's rate, as a tariff's Services section
                    edits it, saved as a SERVICE_RATE row on each GE tariff. emptyInputBehavior="null"
                    makes a cleared Rate read as none; the library's default reads it as $0. */}
                {serviceRatesEnabled ? (
                    <CurrencyTextField
                        fullWidth
                        variant="outlined"
                        label="Rate"
                        textAlign="left"
                        minimumValue="0"
                        emptyInputBehavior="null"
                        disabled={rateField.disabled}
                        value={values.rate}
                        onChange={(_, value) => setField('rate')(value)}
                        helperText={rateField.helperText}
                        css={spaced}
                    />
                ) : null}
                <div css={toggleField}>
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                disabled={locked}
                                checked={values.requireConfirm}
                                onChange={(e) => setField('requireConfirm')(e.target.checked)}
                                inputProps={{ 'aria-label': 'Requires confirmation' }}
                            />
                        }
                        label="Requires confirmation"
                    />
                    <GreyBody css={helperText}>The driver must confirm this service in the mobile app.</GreyBody>
                </div>
                <div css={toggleField}>
                    <FormControlLabel
                        control={
                            <Switch
                                color="primary"
                                disabled={nonBillableLocked || locked}
                                checked={values.billable}
                                onChange={(e) => setField('billable')(e.target.checked)}
                                inputProps={{ 'aria-label': 'Billable' }}
                            />
                        }
                        label="Billable"
                    />
                    <GreyBody css={helperText}>
                        {nonBillableLocked
                            ? 'Off because this service is scoped — clear the scope to bill it.'
                            : "Turn off for warranty / no-charge services. They're still performed and shown to the driver, but the shipper isn't charged."}
                    </GreyBody>
                </div>
            </ModalContent>
            <ModalActions>
                <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
                <PrimaryButton disabled={!canSave || loading} onClick={() => onSave(payload(), ratePayload())}>
                    {isEdit ? 'Save' : 'Add'}
                </PrimaryButton>
            </ModalActions>
        </NavResponsiveModal>
    );
};

export default ServiceCatalogModal;
